<template>
  <div class="positions-container">
    <div class="page-header">
      <h1>职位管理</h1>
      <div class="header-actions">
        <el-input
          v-model="searchQuery"
          placeholder="搜索职位..."
          prefix-icon="Search"
          clearable
          @clear="loadPositions"
          @keyup.enter="searchPositions"
          class="search-input"
        />
        <el-button type="primary" @click="showAddDialog">添加职位</el-button>
      </div>
    </div>

    <div class="positions-content">
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6" v-for="position in positions" :key="position.id">
          <position-item :position="position" @click="handlePositionClick(position)" />
        </el-col>
      </el-row>

      <div v-if="positions.length === 0" class="empty-state">
        <el-empty description="没有找到职位" />
      </div>

      <div class="pagination-container" v-if="total > 0">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[12, 24, 36, 48]"
          layout="total, sizes, prev, pager, next"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑职位' : '添加职位'"
      width="60%"
      destroy-on-close
    >
      <position-form
        v-if="dialogVisible"
        :position="currentPosition"
        :loading="loading"
        @submit="handleFormSubmit"
        @cancel="dialogVisible = false"
      />
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, reactive } from 'vue';
import { ElMessage } from 'element-plus';
import { useRouter } from 'vue-router';
import { usePositionStore } from '@/store/positionStore';
import type { Position } from '@/types/position';
import PositionItem from '@/components/position/PositionItem.vue';
import PositionForm from '@/components/position/PositionForm.vue';

const router = useRouter();
const positionStore = usePositionStore();

const positions = ref<Position[]>([]);
const total = ref(0);
const currentPage = ref(1);
const pageSize = ref(12);
const searchQuery = ref('');
const dialogVisible = ref(false);
const isEdit = ref(false);
const currentPosition = ref<Partial<Position>>({});
const loading = ref(false);

onMounted(() => {
  loadPositions();
});

const loadPositions = async () => {
  try {
    await positionStore.queryPositions({
      page: currentPage.value,
      size: pageSize.value
    });
    positions.value = positionStore.positions;
    total.value = positionStore.total;
  } catch (error) {
    ElMessage.error('加载职位列表失败');
    console.error(error);
  }
};

const searchPositions = async () => {
  if (!searchQuery.value.trim()) {
    return loadPositions();
  }
  
  try {
    await positionStore.searchPositions({
      keyword: searchQuery.value,
      page: currentPage.value,
      size: pageSize.value
    });
    positions.value = positionStore.positions;
    total.value = positionStore.total;
  } catch (error) {
    ElMessage.error('搜索职位失败');
    console.error(error);
  }
};

const handleSizeChange = (size: number) => {
  pageSize.value = size;
  loadPositions();
};

const handleCurrentChange = (page: number) => {
  currentPage.value = page;
  loadPositions();
};

const showAddDialog = () => {
  isEdit.value = false;
  currentPosition.value = {
    applicationStatus: 'not_started',
    interviewStatus: 'not_scheduled',
    workType: 'full_time',
    priority: 2,
    salaryCurrency: 'CNY'
  };
  dialogVisible.value = true;
};

const handlePositionClick = (position: Position) => {
  router.push(`/resources/positions/${position.id}`);
};

const handleFormSubmit = async (position: Partial<Position>) => {
  try {
    if (isEdit.value && position.id) {
      await positionStore.updatePosition(position);
      ElMessage.success('职位更新成功');
    } else {
      await positionStore.createPosition(position);
      ElMessage.success('职位添加成功');
    }
    dialogVisible.value = false;
    loadPositions();
  } catch (error) {
    ElMessage.error(isEdit.value ? '更新职位失败' : '添加职位失败');
    console.error(error);
  }
};
</script>

<style scoped>
.positions-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.search-input {
  width: 250px;
}

.positions-content {
  margin-bottom: 24px;
}

.empty-state {
  margin: 40px 0;
  display: flex;
  justify-content: center;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}
</style> 